import DOMPurify from 'dompurify';
import { marked } from 'marked';

marked.use({
  breaks: true,
  gfm: true,
  renderer: {
    heading({ tokens, depth }) {
      const body = this.parser.parseInline(tokens);
      const tag = depth <= 2 ? 'h2' : 'h3';
      const cls =
        depth <= 2
          ? 'mt-2.5 mb-1 text-base font-semibold text-slate-900 dark:text-slate-100'
          : 'mt-2.5 mb-1 text-sm font-semibold text-slate-900 dark:text-slate-100';
      return `<${tag} class="${cls}">${body}</${tag}>`;
    },

    paragraph({ tokens }) {
      const body = this.parser.parseInline(tokens);
      return `<p class="my-1.5 leading-relaxed text-slate-800 dark:text-slate-200">${body}</p>`;
    },

    list({ items, ordered }) {
      const tag = ordered ? 'ol' : 'ul';
      const cls = ordered
        ? 'my-1.5 list-decimal space-y-1 ps-5 text-slate-800 dark:text-slate-200'
        : 'my-1.5 list-disc space-y-1 ps-4 text-slate-800 dark:text-slate-200';
      const inner = items
        .map((item) => {
          const text = this.parser.parse(item.tokens, !!item.loose);
          return `<li class="leading-relaxed">${text}</li>`;
        })
        .join('');
      return `<${tag} class="${cls}">${inner}</${tag}>`;
    },

    strong({ tokens }) {
      const body = this.parser.parseInline(tokens);
      return `<strong>${body}</strong>`;
    },

    em({ tokens }) {
      const body = this.parser.parseInline(tokens);
      return `<em>${body}</em>`;
    },

    codespan({ text }) {
      return `<code class="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-mono text-pink-600 dark:bg-slate-700 dark:text-pink-400">${text}</code>`;
    },

    code({ text }) {
      return `<pre class="my-2 overflow-x-auto rounded-lg bg-slate-900 p-3 text-xs leading-relaxed"><code class="font-mono text-slate-100">${text}</code></pre>`;
    },

    hr() {
      return '<hr class="my-2 border-slate-200 dark:border-slate-600" />';
    },

    blockquote({ tokens }) {
      const body = this.parser.parse(tokens);
      return `<blockquote class="my-2 border-l-4 border-slate-300 ps-3 text-slate-600 dark:border-slate-600 dark:text-slate-400">${body}</blockquote>`;
    },

    table({ header, rows }) {
      const ths = header
        .map((cell) => {
          const body = this.parser.parseInline(cell.tokens);
          return `<th class="border border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs font-semibold text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100">${body}</th>`;
        })
        .join('');
      const trs = rows
        .map((row) => {
          const tds = row
            .map((cell) => {
              const body = this.parser.parseInline(cell.tokens);
              return `<td class="border border-slate-200 px-3 py-2 align-top text-xs leading-relaxed text-slate-700 dark:border-slate-600 dark:text-slate-300">${body}</td>`;
            })
            .join('');
          return `<tr>${tds}</tr>`;
        })
        .join('');
      return `<div class="my-3 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700"><table class="w-full min-w-[28rem] border-collapse text-sm"><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table></div>`;
    },

    link({ href, tokens }) {
      const body = this.parser.parseInline(tokens);
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">${body}</a>`;
    },
  },
});

function injectPageLinks(html: string): string {
  return html.replace(
    /\[page:(\d+)\]/gi,
    '<a href="#" data-go-to-page="$1" class="inline-flex items-center gap-0.5 rounded bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-600 no-underline hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 cursor-pointer transition-colors" title="Aller à la page $1">p.$1</a>',
  );
}

/**
 * Converts markdown AI responses to sanitised HTML with Tailwind classes.
 */
export function designerAiMessageToSafeHtml(raw: string): string {
  if (!raw || typeof raw !== 'string') return '';
  const text = raw.trim();
  if (!text) return '';

  const html = marked.parse(text, { async: false }) as string;
  const withPages = injectPageLinks(html);

  return DOMPurify.sanitize(withPages, {
    ALLOWED_TAGS: [
      'p', 'strong', 'em', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4',
      'br', 'code', 'pre', 'a', 'hr', 'span', 'blockquote', 'del',
      'table', 'thead', 'tbody', 'tr', 'th', 'td', 'div',
    ],
    ALLOWED_ATTR: ['href', 'class', 'target', 'rel', 'data-go-to-page', 'title'],
  });
}
